import { useState, useRef, useEffect, useCallback } from "react";
import { X, Send, ChevronDown, MessageSquare } from "lucide-react";

/**
 * TeamReplyPanel — Right sidebar panel showing a reply thread.
 *
 * Visual language (matches SharedArticleCard):
 *   - Original message and input wrapper sit in `rounded-xl` cards
 *     with `border border-surfaceBorder` + `bg-dropdownBg/60` +
 *     `backdrop-blur-sm` for soft floating-card feel.
 *   - Avatar bubbles use `bg-velBlue/15` for own/origin, neutral
 *     muted bubble for others.
 *   - Reply messages flow inline (conversational) like the main thread.
 */

const SNIPPET_LENGTH = 200;
const NEAR_BOTTOM_PX = 100;

const TeamReplyPanel = ({
  originalMessage,
  initialReplies = [],
  members = {},
  userUuid,
  teamId,
  onSend,
  onClose,
}) => {
  const [replies, setReplies] = useState(initialReplies);
  const [content, setContent] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [newReplyCount, setNewReplyCount] = useState(0);
  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const hasScrolledToUnread = useRef(false);
  const originalMsgId = originalMessage?.message_id;

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 150);
  }, [originalMsgId]);

  const checkNearBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      const nearBottom = checkNearBottom();
      isNearBottomRef.current = nearBottom;
      if (nearBottom && newReplyCount > 0) setNewReplyCount(0);
    };
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [checkNearBottom, newReplyCount]);

  useEffect(() => {
    if (hasScrolledToUnread.current) return;
    if (replies.length === 0) return;
    hasScrolledToUnread.current = true;
    setTimeout(() => {
      const el = document.getElementById("reply-unread-separator");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        const container = scrollRef.current;
        if (container)
          container.scrollTo({
            top: container.scrollHeight,
            behavior: "instant",
          });
      }
    }, 100);
  }, [replies.length]);

  useEffect(() => {
    const handler = (e) => {
      const msg = e.detail;
      if (!msg || msg.team_id !== teamId) return;
      if (msg.reply_to?.message_id !== originalMsgId) return;
      setReplies((prev) => {
        if (prev.some((m) => m.message_id === msg.message_id)) return prev;
        return [...prev, msg];
      });
      if (isNearBottomRef.current) {
        requestAnimationFrame(() => {
          const el = scrollRef.current;
          if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
        });
      } else {
        setNewReplyCount((prev) => prev + 1);
      }
    };
    window.addEventListener("team:new-message", handler);
    return () => window.removeEventListener("team:new-message", handler);
  }, [teamId, originalMsgId]);

  useEffect(() => {
    const handler = (e) => {
      const data = e.detail;
      if (data.team_id !== teamId) return;
      setReplies((prev) =>
        prev.map((msg) =>
          msg.message_id === data.message_id
            ? {
                ...msg,
                content: data.content,
                edited: true,
                edited_at: data.edited_at,
              }
            : msg,
        ),
      );
    };
    window.addEventListener("team:message-edited", handler);
    return () => window.removeEventListener("team:message-edited", handler);
  }, [teamId]);

  useEffect(() => {
    const handler = (e) => {
      const data = e.detail;
      if (data.team_id !== teamId) return;
      setReplies((prev) =>
        prev.map((msg) =>
          msg.message_id === data.message_id
            ? { ...msg, deleted: true, content: "" }
            : msg,
        ),
      );
    };
    window.addEventListener("team:message-deleted", handler);
    return () => window.removeEventListener("team:message-deleted", handler);
  }, [teamId]);

  const handleNewReplyClick = useCallback(() => {
    setNewReplyCount(0);
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, []);

  if (!originalMessage) return null;

  const origSender = members[originalMessage.sender_uuid] || {};
  const origName =
    originalMessage.sender_name ||
    origSender.display_name ||
    origSender.first_name ||
    "Unknown";
  const origInitial = (origName[0] || "?").toUpperCase();
  const origTime = _formatTime(originalMessage.timestamp);
  const origContent = originalMessage.content || "";
  const isLong = origContent.length > SNIPPET_LENGTH;
  const displayContent =
    !isLong || expanded
      ? origContent
      : origContent.slice(0, SNIPPET_LENGTH) + "…";

  const firstUnreadIndex = replies.findIndex(
    (r) =>
      !r.deleted &&
      r.sender_uuid !== userUuid &&
      !r.read_by?.includes(userUuid),
  );

  const handleSend = () => {
    const trimmed = content.trim();
    if (!trimmed) return;
    onSend?.(trimmed, originalMsgId);
    setContent("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    if (e.key === "Escape") {
      onClose?.();
    }
  };

  return (
    <div className="flex flex-col h-full font-poppins font-normal text-mainText">
      {/* Panel header — flat, signals "this is a panel control surface" */}
      <div className="flex items-center justify-between px-4 py-5 border-b border-surfaceBorder shrink-0">
        <div className="flex items-center gap-x-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-velBlue/15 shrink-0">
            <MessageSquare
              className="h-3.5 w-3.5 text-velBlue"
              strokeWidth={2}
            />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="text-[13px] font-normal text-mainText">
              Thread
            </span>
            {replies.length > 0 && (
              <span className="text-[11px] font-light text-mutedText/80">
                {replies.length} repl{replies.length === 1 ? "y" : "ies"}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={onClose}
          className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-mainText/[0.06] transition text-mutedText hover:text-mainText cursor-pointer outline-none ring-inputFocus/60 focus-visible:ring-2"
        >
          <X className="h-4 w-4" strokeWidth={2} />
        </button>
      </div>

      {/* Original message — rounded card, the focal "thing being
          replied to". Same framing as SharedArticleCard. */}
      <div className="px-3 pt-3 pb-2 shrink-0">
        <div className="rounded-xl border border-surfaceBorder bg-dropdownBg/60 backdrop-blur-sm p-3.5">
          <div className="flex items-start gap-x-3">
            <div className="w-7 h-7 rounded-full bg-velBlue/15 flex items-center justify-center text-[12px] font-normal text-velBlue shrink-0">
              {origInitial}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-x-2 mb-1">
                <span className="text-[13px] font-normal text-mainText">
                  {origName}
                </span>
                <span className="text-[10px] font-light text-mutedText/60">
                  {origTime}
                </span>
              </div>
              <p className="text-[13px] font-normal text-mutedText leading-relaxed whitespace-pre-wrap break-words">
                {displayContent}
              </p>
              {isLong && (
                <button
                  onClick={() => setExpanded((prev) => !prev)}
                  className="text-[11px] font-light text-velBlue hover:text-velBlue/80 mt-1.5 transition cursor-pointer"
                >
                  {expanded ? "Show less" : "Show more"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Replies list — scrollable, conversational flow */}
      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain relative"
      >
        {replies.length === 0 ? (
          <div className="flex items-center justify-center h-full px-6">
            <p className="text-[13px] font-light text-mutedText/80 text-center">
              No replies yet — be the first to reply
            </p>
          </div>
        ) : (
          <div className="px-4 py-2 space-y-0">
            {replies.map((reply, i) => {
              const sender = members[reply.sender_uuid] || {};
              const name =
                reply.sender_name ||
                sender.display_name ||
                sender.first_name ||
                "Unknown";
              const initial = (name[0] || "?").toUpperCase();
              const isOwn = reply.sender_uuid === userUuid;
              const time = _formatTime(reply.timestamp);

              const prev = i > 0 ? replies[i - 1] : null;
              const isGrouped =
                prev &&
                prev.sender_uuid === reply.sender_uuid &&
                reply.timestamp - prev.timestamp < 120;

              const showUnreadSep =
                i === firstUnreadIndex && firstUnreadIndex > 0;

              return (
                <div key={reply.message_id}>
                  {showUnreadSep && (
                    <div
                      id="reply-unread-separator"
                      className="flex items-center gap-x-2 py-2"
                    >
                      <div className="flex-1 h-px bg-velBlue/50" />
                      <span className="text-[10px] font-light text-velBlue whitespace-nowrap">
                        New
                      </span>
                      <div className="flex-1 h-px bg-velBlue/50" />
                    </div>
                  )}

                  <div
                    className={[
                      "flex gap-x-2.5",
                      isGrouped ? "pt-0.5" : "pt-2.5",
                    ].join(" ")}
                  >
                    <div className="w-7 shrink-0">
                      {!isGrouped && (
                        <div
                          className={[
                            "w-7 h-7 rounded-full flex items-center justify-center text-[12px] font-normal",
                            isOwn
                              ? "bg-velBlue/15 text-velBlue"
                              : "bg-mutedText/20 text-mutedText",
                          ].join(" ")}
                        >
                          {initial}
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      {!isGrouped && (
                        <div className="flex items-baseline gap-x-1.5 mb-0.5">
                          <span
                            className={[
                              "text-[13px] font-normal",
                              isOwn ? "text-velBlue" : "text-mainText",
                            ].join(" ")}
                          >
                            {name}
                          </span>
                          <span className="text-[10px] font-light text-mutedText/60">
                            {time}
                          </span>
                        </div>
                      )}

                      {reply.deleted ? (
                        <p className="text-sm font-normal italic text-mutedText/60 leading-relaxed">
                          This message was deleted
                        </p>
                      ) : (
                        <p className="text-sm font-normal text-mainText leading-relaxed whitespace-pre-wrap break-words">
                          {reply.content}
                          {reply.edited && (
                            <span className="ml-1 text-[10px] font-light text-mutedText/60 italic">
                              (edited)
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Floating "new replies" pill */}
        {newReplyCount > 0 && (
          <button
            onClick={handleNewReplyClick}
            className="sticky bottom-2 left-1/2 -translate-x-1/2 z-20 flex items-center gap-x-1.5 px-3 py-1.5 rounded-full bg-velBlue hover:bg-velBlue/80 text-white text-[11px] font-normal shadow-login-panel transition-all mx-auto cursor-pointer"
            style={{ animation: "reply-btn-in 200ms ease-out" }}
          >
            <ChevronDown className="h-3 w-3" strokeWidth={2.5} />
            {newReplyCount} new repl{newReplyCount === 1 ? "y" : "ies"}
          </button>
        )}
      </div>

      {/* Reply input — rounded card framing */}
      <div className="px-3 pt-1 pb-3 shrink-0">
        <div className="flex items-end gap-x-2 p-2 rounded-xl border border-surfaceBorder bg-dropdownBg/60 backdrop-blur-sm transition focus-within:border-inputFocus/60 focus-within:ring-2 focus-within:ring-inputFocus/40">
          <textarea
            ref={inputRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`Reply to ${origName}...`}
            rows={2}
            className="flex-1 text-sm font-normal bg-transparent text-mainText placeholder:text-mutedText/70 resize-none focus:outline-none leading-relaxed px-1 py-0.5"
          />
          <button
            onClick={handleSend}
            disabled={!content.trim()}
            className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition shrink-0 cursor-pointer"
          >
            <Send className="h-3.5 w-3.5 text-white" strokeWidth={2} />
          </button>
        </div>
        <p className="mt-1.5 px-1 text-[10px] font-light text-mutedText/70">
          Enter to send · Esc to close
        </p>
      </div>

      <style>{`
        @keyframes reply-btn-in {
          from { opacity: 0; transform: translateX(-50%) translateY(8px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </div>
  );
};

function _formatTime(ts) {
  try {
    const d = new Date(ts * 1000);
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

export default TeamReplyPanel;
